// components/form/fields/SelectField.tsx
import { useFormContext } from "react-hook-form";
import { type FormField } from "../formFieldType";
import { useEffect } from "react";

type SelectOption = string | { label: string; value: string | number };

export function SelectField({ field }: { field: FormField & { options?: any[]; dependsOn?: string; optionsMap?: Record<string, any[]> } }) {
  const { register, formState, watch, setValue } = useFormContext();

  // If this field depends on another field, watch its value
  const parentValue = field.dependsOn ? watch(field.dependsOn) : undefined;

  // Determine options based on dependency
  const options = field.dependsOn && field.optionsMap
    ? field.optionsMap[parentValue] || []
    : field.options || [];

  // Reset value if parent changes
  useEffect(() => {
    if (field.dependsOn) setValue(field.name, ""); 
  }, [parentValue]);

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.label} {field.required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex justify-between items-start gap-2">
        <div className="w-full">
          <select
            {...register(field.name)}
            name={field.name}
            required={field.required}
            disabled={field.disabled || (field.dependsOn && !parentValue)}
            defaultValue=""
            className={`
              w-full border border-gray-400 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none text-gray-900
              ${field.disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}
            `}
          >
            {field.placeholder && (
              <option value="" disabled>
                {field.placeholder}
              </option>
            )}
            {options.map((opt) => {
              if (typeof opt === "string") return <option key={opt} value={opt}>{opt}</option>;
              return <option key={opt.value} value={opt.value}>{opt.label}</option>;
            })}
          </select>
        </div>
        {field.button && <div className="w-[100px] flex justify-end items-end">{field.button}</div>}
      </div>
      {formState.errors[field.name] && (
        <p className="text-red-500">{String(formState.errors[field.name]?.message)}</p>
      )}
    </div>
  );
}